import React, { useState } from 'react';
import { CalendarIcon, MapPinIcon, UsersIcon, TagIcon, SearchIcon, FilterIcon, StarIcon, HeartIcon, PlusIcon, GridIcon, ListIcon, ChevronRightIcon, GlobeIcon, XIcon } from 'lucide-react';
import { useAppContext } from '../context/AppContext';
export function EventsPage() {
  const {
    events,
    toggleEventInterest
  } = useAppContext();
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterIndustry, setFilterIndustry] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const filteredEvents = events.filter(evt => {
    const matchesSearch = searchQuery === '' || evt.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = filterCategory === 'all' || evt.category === filterCategory;
    const matchesIndustry = filterIndustry === 'all' || evt.industry.includes(filterIndustry);
    return matchesSearch && matchesCategory && matchesIndustry;
  });
  const featuredEvents = filteredEvents.filter(e => e.featured);
  const regularEvents = filteredEvents.filter(e => !e.featured);
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Conference':
        return 'bg-blue-100 text-blue-700';
      case 'Training':
        return 'bg-emerald-100 text-emerald-700';
      case 'Summit':
        return 'bg-purple-100 text-purple-700';
      case 'Partner Event':
        return 'bg-gold-100 text-gold-700';
      default:
        return 'bg-slate-100 text-slate-700';
    }
  };
  return <div className="space-y-6" data-id="element-1314">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-1315">
        <div data-id="element-1316">
          <h2 className="text-2xl font-bold text-navy-900" data-id="element-1317">
            Events & Conferences
          </h2>
          <p className="text-slate-500" data-id="element-1318">
            Discover industry events and link them to your travel plans.
          </p>
        </div>
        <button onClick={() => setShowPublishModal(true)} className="flex items-center rounded-lg bg-navy-900 px-4 py-2 text-sm font-medium text-white hover:bg-navy-700 transition-colors" data-id="element-1319">
          <PlusIcon className="mr-2 h-4 w-4" data-id="element-1320" />
          Publish Event
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3" data-id="element-1321">
        <div className="relative flex-1" data-id="element-1322">
          <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" data-id="element-1323" />
          <input type="text" placeholder="Search events..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="h-10 w-full rounded-lg border border-slate-300 bg-white pl-10 pr-4 text-sm focus:border-navy-600 focus:outline-none focus:ring-1 focus:ring-navy-600" data-id="element-1324" />
        </div>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm focus:border-navy-600 focus:outline-none" data-id="element-1325">
          <option value="all" data-id="element-1326">All Categories</option>
          <option value="Conference" data-id="element-1327">Conferences</option>
          <option value="Training" data-id="element-1328">Trainings</option>
          <option value="Summit" data-id="element-1329">Summits</option>
          <option value="Partner Event" data-id="element-1330">Partner Events</option>
        </select>
        <select value={filterIndustry} onChange={e => setFilterIndustry(e.target.value)} className="h-10 rounded-lg border border-slate-300 bg-white px-4 text-sm focus:border-navy-600 focus:outline-none" data-id="element-1331">
          <option value="all" data-id="element-1332">All Industries</option>
          <option value="Pharmaceutical" data-id="element-1333">Pharmaceutical</option>
          <option value="Technology" data-id="element-1334">Technology</option>
          <option value="Finance" data-id="element-1335">Finance</option>
          <option value="Healthcare" data-id="element-1336">Healthcare</option>
          <option value="Travel" data-id="element-1337">Travel</option>
          <option value="Corporate" data-id="element-1338">Corporate</option>
        </select>
      </div>

      {/* Featured Events Banner */}
      {featuredEvents.length > 0 && <div className="space-y-4" data-id="element-1339">
          <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider" data-id="element-1340">
            Featured Events
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1341">
            {featuredEvents.map(event => <div key={event.id} className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-sm group" data-id="element-1342">
                <div className="h-48 bg-cover bg-center" style={{
            backgroundImage: `url(${event.image})`
          }} data-id="element-1343">
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-900/90 via-navy-900/40 to-transparent" data-id="element-1344" />
                  <div className="absolute top-4 left-4" data-id="element-1345">
                    <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-gold-500 text-navy-900 uppercase" data-id="element-1346">
                      ★ Featured
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-5" data-id="element-1347">
                    <div className="flex items-center gap-2 mb-2" data-id="element-1348">
                      <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getCategoryColor(event.category)}`} data-id="element-1349">
                        {event.category}
                      </span>
                      {event.industry.map(ind => <span key={ind} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-white/20 text-white" data-id="element-1350">
                          {ind}
                        </span>)}
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1" data-id="element-1351">
                      {event.title}
                    </h3>
                    <div className="flex items-center gap-4 text-xs text-white/80" data-id="element-1352">
                      <span className="flex items-center gap-1" data-id="element-1353">
                        <CalendarIcon className="h-3 w-3" data-id="element-1354" />
                        {event.date}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-1355">
                        <MapPinIcon className="h-3 w-3" data-id="element-1356" />
                        {event.location}
                      </span>
                      <span className="flex items-center gap-1" data-id="element-1357">
                        <UsersIcon className="h-3 w-3" data-id="element-1358" />
                        {event.attendeeCount} interested
                      </span>
                    </div>
                  </div>
                </div>
                <div className="p-4 bg-white flex items-center justify-between" data-id="element-1359">
                  <p className="text-xs text-slate-600 line-clamp-1 flex-1 mr-4" data-id="element-1360">
                    {event.description}
                  </p>
                  <button onClick={() => toggleEventInterest(event.id)} className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-colors ${event.interested ? 'bg-navy-900 text-white' : 'border border-navy-300 text-navy-700 hover:bg-navy-50'}`} data-id="element-1361">
                    <HeartIcon className={`h-3.5 w-3.5 ${event.interested ? 'fill-current' : ''}`} data-id="element-1362" />
                    {event.interested ? 'Interested' : 'Show Interest'}
                  </button>
                </div>
              </div>)}
          </div>
        </div>}

      {/* Regular Events Grid */}
      <div data-id="element-1363">
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-4" data-id="element-1364">
          All Events
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-1365">
          {regularEvents.map(event => <div key={event.id} className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-id="element-1366">
              <div className="h-36 bg-cover bg-center relative" style={{
            backgroundImage: `url(${event.image})`
          }} data-id="element-1367">
                <div className="absolute top-3 left-3" data-id="element-1368">
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${getCategoryColor(event.category)}`} data-id="element-1369">
                    {event.category}
                  </span>
                </div>
              </div>
              <div className="p-4" data-id="element-1370">
                <h4 className="font-bold text-sm text-slate-900 mb-1 line-clamp-1" data-id="element-1371">
                  {event.title}
                </h4>
                <p className="text-xs text-slate-500 mb-3 line-clamp-2" data-id="element-1372">
                  {event.description}
                </p>
                <div className="space-y-1.5 mb-3" data-id="element-1373">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-1374">
                    <CalendarIcon className="h-3 w-3 text-slate-400" data-id="element-1375" />
                    {event.date} – {event.endDate}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-1376">
                    <MapPinIcon className="h-3 w-3 text-slate-400" data-id="element-1377" />
                    {event.location}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs text-slate-600" data-id="element-1378">
                    <UsersIcon className="h-3 w-3 text-slate-400" data-id="element-1379" />
                    {event.attendeeCount} interested
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-3" data-id="element-1380">
                  {event.industry.map(ind => <span key={ind} className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600" data-id="element-1381">
                      {ind}
                    </span>)}
                </div>
                <button onClick={() => toggleEventInterest(event.id)} className={`w-full flex items-center justify-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-colors ${event.interested ? 'bg-navy-900 text-white' : 'border border-navy-300 text-navy-700 hover:bg-navy-50'}`} data-id="element-1382">
                  <HeartIcon className={`h-3.5 w-3.5 ${event.interested ? 'fill-current' : ''}`} data-id="element-1383" />
                  {event.interested ? 'Interested' : 'Show Interest'}
                </button>
              </div>
            </div>)}
        </div>
      </div>

      {/* Publish Event Modal */}
      {showPublishModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm" data-id="element-1384">
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" data-id="element-1385">
            <div className="flex items-center justify-between mb-6" data-id="element-1386">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-1387">
                Publish New Event
              </h3>
              <button onClick={() => setShowPublishModal(false)} className="rounded-full p-1 hover:bg-slate-100" data-id="element-1388">
                <XIcon className="h-5 w-5 text-slate-500" data-id="element-1389" />
              </button>
            </div>
            <div className="space-y-4" data-id="element-1390">
              <div data-id="element-1391">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1392">
                  Event Title
                </label>
                <input type="text" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="e.g. Annual Tech Summit 2025" data-id="element-1393" />
              </div>
              <div data-id="element-1394">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1395">
                  Description
                </label>
                <textarea rows={3} className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="Describe the event..." data-id="element-1396" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1397">
                <div data-id="element-1398">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1399">
                    Start Date
                  </label>
                  <input type="date" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-1400" />
                </div>
                <div data-id="element-1401">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1402">
                    End Date
                  </label>
                  <input type="date" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-1403" />
                </div>
              </div>
              <div data-id="element-1404">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1405">
                  Location
                </label>
                <input type="text" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="e.g. New York, NY" data-id="element-1406" />
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-1407">
                <div data-id="element-1408">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1409">
                    Category
                  </label>
                  <select className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-1410">
                    <option data-id="element-1411">Conference</option>
                    <option data-id="element-1412">Training</option>
                    <option data-id="element-1413">Summit</option>
                    <option data-id="element-1414">Partner Event</option>
                  </select>
                </div>
                <div data-id="element-1415">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1416">
                    Industry
                  </label>
                  <select className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" data-id="element-1417">
                    <option data-id="element-1418">Technology</option>
                    <option data-id="element-1419">Pharmaceutical</option>
                    <option data-id="element-1420">Finance</option>
                    <option data-id="element-1421">Healthcare</option>
                  </select>
                </div>
              </div>
              <div data-id="element-1422">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1423">
                  Image URL
                </label>
                <input type="url" className="w-full rounded-lg border border-slate-300 p-2.5 text-sm" placeholder="https://..." data-id="element-1424" />
              </div>
              <div className="flex gap-3 pt-2" data-id="element-1425">
                <button onClick={() => setShowPublishModal(false)} className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1426">
                  Cancel
                </button>
                <button onClick={() => setShowPublishModal(false)} className="flex-1 rounded-lg bg-navy-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-700" data-id="element-1427">
                  Publish Event
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}